import debounce from "lodash.debounce";
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { ItemModelV2 } from "../../../inventory/models/item_model";
import { fetchGet, toQueryString } from "../../../../service/api_client";
import { MasterItemStatus } from "../../../../utils/enums";
import POAutoCompleteField from "./po_auto_complete_widget";

interface ItemSearchFieldPropsV2 {
  onSelect: (data: ItemModelV2) => void;
  disabled?: boolean;
  placeholder?: string;
  value?: ItemModelV2;
}

const POItemSearchFieldV2: FunctionComponent<ItemSearchFieldPropsV2> = (
  props,
) => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items`;
  const { showToast } = useToast();
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [addNew, setAddNew] = useState(false);
  const [data, setData] = useState<ItemModelV2[]>();

  const getData = async (prop: {
    item_name?: string;
    item_code?: string;
    mpn?: string;
    page: number;
    count: number;
    status: string;
    all?: boolean;
  }) => {
    prop.status = MasterItemStatus.Approved;
    setLoading(true);
    try {
      const res = await fetchGet(url + toQueryString(prop));
      if (res.success) {
        setData(res.data.data);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    }
    setLoading(false);
  };

  const debouncedHandler = useCallback(
    debounce((query) => {
      if (query)
        getData({
          item_name: query,
          item_code: query,
          mpn: query,
          page: 1,
          count: 1,
          all: true,
          status: MasterItemStatus.Approved,
        });
      else setData(undefined);
    }, 500),
    [],
  );

  useEffect(() => {
    if (search && edit) {
      if (props.value && props.value.name === search) return;
      debouncedHandler(search);
    }
  }, [search]);

  useEffect(() => {
    if (props.value && Object.keys(props.value).length > 0) {
      setSearch(props.value.name);
    }
  }, [props.value]);

  useEffect(() => {
    if (props.disabled) {
      setEdit(false);
      if (props.value && Object.keys(props.value).length > 0) {
        setSearch(props.value.name);
      }
    }
  }, [props.disabled]);

  return (
    <div className="flex gap-1 items-center w-full h-16">
      {edit ? (
        <POAutoCompleteField
          disabled={props.disabled}
          onAddNew={() => setAddNew(true)}
          loading={loading}
          value={search}
          placeholder={props.placeholder ?? "Select an item"}
          onSelect={(val) => {
            setEdit(false);
            setSearch(val.value.name);
            props.onSelect(val.value);
          }}
          suggestions={data?.map((d) => ({
            label: (
              <div className="text-xs">
                <b>{d.name}</b>
                {" - "}
                {d.mpn && (
                  <>
                    <span className="text-gray-700">{d.mpn}</span>
                    {" - "}
                  </>
                )}
                <span className="text-gray-700 italic">{d.description}</span>
              </div>
            ),
            value: d,
          }))}
          onChange={setSearch}
        />
      ) : (
        <React.Fragment>
          {props.value?.name && (
            <span
              onClick={(e) => {
                e.stopPropagation(); // Prevent edit mode
                navigator.clipboard.writeText(props.value?.name ?? "");
                showToast({
                  type: ToastType.info,
                  text: "Copied to clipboard!",
                });
              }}
              title="Copy to clipboard"
              className="text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              📋
            </span>
          )}
          <div
            title={props.value?.description || ""}
            className={`text-sm py-3 h-16 rounded focus:outline-none w-full border break-words ${
              search ? "break-words overflow-y-auto" : "text-gray-400"
            }`}
            style={{
              maxWidth: "100%",
              overflowX: "auto",
            }}
            onClick={() => {
              if (!props.disabled) {
                setEdit(true);
              }
            }}
          >
            <span className="flex-1 text-left break-words w-full text-xs">
              {props.value
                ? `${props.value.name} - ${props.value.description || ""}`
                : props.placeholder || "Select an item"}
            </span>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default POItemSearchFieldV2;
